import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadSession } from "../auth";
import { getTrainingById, isCertificateAllowed } from "../services/api";
import { shouldBlockFeatureLoads } from "../utils/onboardingGate";
import Button from "../components/Button";
import { useTranslation, Trans } from "react-i18next";

export default function CertificatePreview() {
  const { id } = useParams();
  const nav = useNavigate();
  const user = loadSession()?.user;
  const { t, i18n } = useTranslation();

  const [training, setTraining] = useState(null);
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [guardStatus, setGuardStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      // If onboarding/tenant gate is active, avoid loading training/certificate previews
      if (shouldBlockFeatureLoads()) {
        nav("/onboarding", { replace: true });
        return;
      }
      try {
        const [tRes, guard] = await Promise.all([
          getTrainingById(id),
          isCertificateAllowed(id) // { allowed, status }
        ]);
        if (!alive) return;
        setTraining(tRes);
        setAllowed(!!guard.allowed);
        setGuardStatus(guard.status || "");
      } catch (e) {
        if (!alive) return;
        setError(e?.message || t("certificate.error_generic"));
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-black/60">
        {t("certificate.checking")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-100 px-4">
        <div className="max-w-md w-full rounded-2xl bg-white border border-black/10 shadow-sm p-6">
          <h1 className="text-lg font-semibold mb-2">{t("certificate.error_title")}</h1>
          <p className="text-sm text-black/70">{error}</p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => nav(-1)}>{t("certificate.btn_back")}</Button>
            <Button variant="ghost" onClick={() => nav("/trainings")}>
              {t("certificate.btn_my_trainings")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-100 px-4">
        <div className="max-w-md w-full rounded-2xl bg-white border border-black/10 shadow-sm p-6">
          <h1 className="text-lg font-semibold mb-2">{t("certificate.finish_first_title")}</h1>
          <p className="text-sm text-black/70">
            <Trans i18nKey="certificate.need_pass_quiz_for" values={{ title: training?.title || id }}>
              You need to pass the quiz for <span className="font-medium">{{ title: training?.title || id }}</span> before downloading the certificate.
            </Trans>
          </p>
          {guardStatus && (
            <p className="mt-2 text-xs text-black/50">
              {t("certificate.current_status", { status: guardStatus })}
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <Button onClick={() => nav(`/training/${id}`)}>{t("certificate.btn_go_to_training")}</Button>
            <Button variant="ghost" onClick={() => nav("/trainings")}>
              {t("certificate.btn_my_trainings")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString(i18n.language);

  return (
    <div className="min-h-screen bg-gray-100 py-10 print:bg-white print:p-0">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow p-8 print:shadow-none print:rounded-none">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <div className="text-sm text-black/60">{t("certificate.preview_label")}</div>
          <Button onClick={() => window.print()}>{t("certificate.btn_print")}</Button>
        </div>

        <div className="text-center border-4 border-black/10 rounded-2xl p-10">
          <div className="text-xs tracking-widest text-black/60 uppercase">
            {t("certificate.certificate_of_completion")}
          </div>
          <h1 className="text-3xl font-bold mt-2">{t("certificate.this_certifies_that")}</h1>
          <div className="text-2xl font-semibold mt-4">
            {user?.name || user?.email}
          </div>
          <div className="mt-6 text-black/70">{t("certificate.has_completed")}</div>
          <div className="text-xl font-semibold mt-1">
            {training?.title || id}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-8 text-left">
            <div>
              <div className="text-xs uppercase text-black/50">{t("certificate.date")}</div>
              <div className="border-b border-black/20 pb-1">{today}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-black/50">
                {t("certificate.instructor")}
              </div>
              <div className="border-b border-black/20 pb-1">
                {t("certificate.provider_name")}
              </div>
            </div>
          </div>

          <div className="mt-10 text-sm text-black/60 print:hidden">
            {t("certificate.print_hint")}
          </div>
        </div>
      </div>
    </div>
  );
}
